'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const roles = [
  {
    icon: 'DevicePhoneMobileIcon',
    label: 'Customer App',
    sub: 'Book, track & rate — iOS & Android',
    color: '#0459F7',
    bg: 'rgba(4,89,247,0.12)',
    border: 'rgba(4,89,247,0.25)',
  },
  {
    icon: 'UserIcon',
    label: 'Driver App',
    sub: 'Navigate, deliver & earn — iOS & Android',
    color: '#059669',
    bg: 'rgba(5,150,105,0.12)',
    border: 'rgba(5,150,105,0.25)',
  },
  {
    icon: 'BuildingStorefrontIcon',
    label: 'Merchant Portal',
    sub: 'Manage orders & catalog — Web',
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.12)',
    border: 'rgba(124,58,237,0.25)',
  },
];

const coreFeatures = [
  {
    num: '01',
    icon: 'DevicePhoneMobileIcon',
    label: 'Customer App for iOS & Android',
    desc: 'Intuitive booking with live driver tracking, order history, one-tap reorder, loyalty rewards, dark mode, and multi-language support — everything a customer needs to trust your brand.',
    color: '#0459F7',
    bg: 'rgba(4,89,247,0.08)',
    tag: 'Customer-facing',
  },
  {
    num: '02',
    icon: 'UserIcon',
    label: 'Driver App with Earnings & Navigation',
    desc: 'Optimized turn-by-turn navigation, live earnings dashboard, shift management, performance score, and delivery task list — built for couriers to maximize efficiency and income.',
    color: '#059669',
    bg: 'rgba(5,150,105,0.08)',
    tag: 'Driver-facing',
  },
  {
    num: '03',
    icon: 'BuildingStorefrontIcon',
    label: 'Merchant Portal for Store Management',
    desc: 'Merchants manage their catalog, process incoming orders, schedule bulk pickups, view live delivery status, and access earnings reports — all from one dedicated web portal.',
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.08)',
    tag: 'Merchant-facing',
  },
  {
    num: '04',
    icon: 'ChatBubbleLeftRightIcon',
    label: 'WhatsApp Conversational Order Booking',
    desc: 'Customers place pickup and delivery requests directly via WhatsApp — no app download required. Chatbot confirms orders, shares live tracking links, and sends ETA updates.',
    color: '#16A34A',
    bg: 'rgba(22,163,74,0.08)',
    tag: 'No App Needed',
  },
  {
    num: '05',
    icon: 'CreditCardIcon',
    label: 'Payment Gateway — UPI, Card & COD',
    desc: 'Accept UPI, credit/debit cards, digital wallets, and cash on delivery. Automated driver payout, commission split, merchant settlement, and invoice generation — all built in.',
    color: '#D97706',
    bg: 'rgba(217,119,6,0.08)',
    tag: 'Multi-modal Payments',
  },
  {
    num: '06',
    icon: 'StarIcon',
    label: 'Ratings & Reviews Feedback Loop',
    desc: 'Customers and merchants rate each delivery. Driver performance scores power smart dispatch — top-rated drivers get priority orders, more earnings, and exclusive incentives.',
    color: '#DC2626',
    bg: 'rgba(220,38,38,0.08)',
    tag: 'Quality Engine',
  },
];

const orderSteps = [
  {
    icon: 'ShoppingCartIcon',
    title: 'Order Placed',
    desc: 'Customer places an order via app, WhatsApp, or merchant website. Parcel details, pickup address, and drop-off confirmed instantly.',
    detail: 'App · WhatsApp · Web',
  },
  {
    icon: 'BoltIcon',
    title: 'Instant Driver Matching',
    desc: 'Platform auto-assigns the nearest available driver based on proximity, vehicle type, current load, and performance rating.',
    detail: 'AI-powered dispatch',
  },
  {
    icon: 'MapPinIcon',
    title: 'Pickup & Transit',
    desc: 'Driver navigates to pickup with real-time guidance. Customer and merchant track every move live. OTP-confirmed pickup ensures security.',
    detail: 'Live GPS tracking',
  },
  {
    icon: 'DocumentTextIcon',
    title: 'Delivery & POD',
    desc: 'Driver delivers at the destination and captures photo or OTP-based Proof of Delivery. Customer notified instantly on completion.',
    detail: 'Photo + OTP confirmation',
  },
  {
    icon: 'CurrencyRupeeIcon',
    title: 'Payment & Payout',
    desc: 'Payment settled, commission auto-split, driver payout triggered, and merchant report updated — all without manual reconciliation.',
    detail: 'Auto settlement',
  },
];

const customerFeatures = [
  'Book Instant or Scheduled Delivery',
  'Real-time GPS Driver Tracking',
  'OTP Pickup & Delivery Confirmation',
  'Multiple Saved Addresses',
  'In-app Chat & Call Masking',
  'Push & WhatsApp Notifications',
  'Order History & One-tap Reorder',
  'Loyalty Points & Referral Program',
  'Fare Estimate Before Booking',
  'Dark Mode & Multi-language',
  'Live ETA with Traffic Updates',
  'Rate & Review Every Delivery',
];

const driverFeatures = [
  'Online / Offline Toggle',
  'Live Order Requests (Accept / Reject)',
  'Optimized Turn-by-turn Navigation',
  'POD Capture — Photo & OTP',
  'Real-time Earnings Dashboard',
  'Daily & Weekly Payout Tracker',
  'Performance Score & Badges',
  'Break Mode & Shift Management',
  'Customer Call Masking',
  'Incentive & Bonus Alerts',
  'Trip History & Route Replay',
  'In-app Support Chat',
];

const merchantFeatures = [
  'Product Catalog & Menu Management',
  'Incoming Order Live Dashboard',
  'Bulk Pickup Scheduling',
  'Live Delivery Status Tracking',
  'Commission & Payout Reports',
  'Customer Ratings & Feedback View',
  'Promo, Discount & Offer Setup',
  'Zone-based Delivery Settings',
  'Invoice & Billing Management',
  'Staff Role & Access Control',
  'API Integration for Inventory',
  'Business Analytics & Reports',
];

const whatsappSteps = [
  { num: '1', text: 'Message the business WhatsApp number' },
  { num: '2', text: 'Share pickup address, drop address & parcel details' },
  { num: '3', text: 'Bot confirms order & fare estimate instantly' },
  { num: '4', text: 'Driver assigned — live tracking link sent' },
  { num: '5', text: 'Delivery confirmed with OTP or photo' },
];

const paymentMethods = [
  { icon: 'DevicePhoneMobileIcon', label: 'UPI', sub: 'GPay, PhonePe, Paytm' },
  { icon: 'CreditCardIcon', label: 'Cards', sub: 'Visa, Mastercard, Rupay' },
  { icon: 'BanknotesIcon', label: 'Cash on Delivery', sub: 'COD with digital receipt' },
  { icon: 'WalletIcon', label: 'Wallets', sub: 'Paytm, Amazon Pay & more' },
  { icon: 'ArrowPathIcon', label: 'Auto Payout', sub: 'Driver settlement daily' },
  { icon: 'DocumentTextIcon', label: 'Invoicing', sub: 'Auto-generated PDF bills' },
];

const additionalFeatures = [
  { icon: 'MapPinIcon', label: 'Geo-fencing', sub: 'Define serviceable zones' },
  { icon: 'BellAlertIcon', label: 'Smart Notifications', sub: 'SMS, push & WhatsApp' },
  { icon: 'CalendarDaysIcon', label: 'Delivery Scheduling', sub: 'Same-day & next-day slots' },
  { icon: 'ArrowPathIcon', label: 'Returns & Reverse Pickup', sub: 'Streamlined reverse flow' },
  { icon: 'ChartBarIcon', label: 'Live Analytics', sub: 'Orders, earnings, performance' },
  { icon: 'ShieldCheckIcon', label: 'Fraud & Safety Controls', sub: 'OTP, photo POD, masking' },
];

const stats = [
  { value: '3 Apps', label: 'Customer · Driver · Merchant', icon: 'DevicePhoneMobileIcon' },
  { value: 'Instant', label: 'AI-powered Driver Matching', icon: 'BoltIcon' },
  { value: '21 Days', label: 'Go-Live Timeline', icon: 'ClockIcon' },
  { value: '99.9%', label: 'Platform Uptime', icon: 'ShieldCheckIcon' },
];

export default function CourierContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO (white + dark right panel) ── */}
      <div ref={heroRef} className="bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-14 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-[#64748B] text-sm font-medium mb-7 hover:text-[#0459F7] transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(245,158,11,0.4)] bg-[rgba(245,158,11,0.08)] text-[11px] font-semibold tracking-widest text-[#D97706] uppercase mb-5">
                <Icon name="BoltIcon" size={11} className="text-[#F59E0B]" />
                Delivery Services
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-[#0F172A] leading-tight tracking-tight mb-5">
                Hyperlocal delivery,{' '}
                <span
                  className="italic font-semibold"
                  style={{ color: '#0459F7', textShadow: '0 0 32px rgba(4,89,247,0.2)' }}
                >
                  done right.
                </span>
              </h1>

              <p className="animate-on-scroll stagger-3 text-[#475569] text-lg leading-relaxed mb-9 max-w-lg">
                A complete courier platform with Customer App, Driver App, and Merchant Portal — plus WhatsApp booking, multi-payment, and an AI-powered dispatch engine.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link href="/demo-booking" className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold">
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-[#475569] border border-[rgba(15,23,42,0.15)] hover:border-[#0459F7] hover:text-[#0459F7] transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['21-day go-live', 'White-label ready', 'iOS & Android apps', 'WhatsApp booking'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-[#64748B] font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#0459F7]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — dark panel with 3 role cards */}
            <div className="animate-on-scroll stagger-3">
              <div className="rounded-3xl overflow-hidden" style={{ background: '#0F172A' }}>
                {/* Panel top bar */}
                <div className="px-7 pt-7 pb-4 border-b border-white/[0.06]">
                  <p className="text-xs font-semibold text-white/30 uppercase tracking-widest">Platform Components</p>
                  <h3 className="font-display text-xl font-semibold text-white mt-1">Three apps. One ecosystem.</h3>
                </div>

                {/* Role cards */}
                <div className="p-5 flex flex-col gap-3">
                  {roles.map((r) => (
                    <div
                      key={r.label}
                      className="flex items-center gap-4 p-4 rounded-2xl border transition-all hover:scale-[1.01]"
                      style={{ background: r.bg, borderColor: r.border }}
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: r.color }}
                      >
                        <Icon name={r.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-white text-sm">{r.label}</p>
                        <p className="text-xs text-white/50 mt-0.5">{r.sub}</p>
                      </div>
                      <Icon name="ChevronRightIcon" size={16} className="text-white/25 ml-auto flex-shrink-0" />
                    </div>
                  ))}
                </div>

                {/* Bottom pulse indicator */}
                <div className="px-7 pb-7 pt-3 flex items-center gap-3">
                  <span className="relative flex h-2 w-2">
                    <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" />
                  </span>
                  <span className="text-xs text-white/40 font-medium">All systems live · Real-time sync across apps</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div className="border-y border-[rgba(15,23,42,0.07)] bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[rgba(15,23,42,0.07)]">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-6`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.12), rgba(10,47,216,0.06))' }}>
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#0459F7]" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-[#0F172A] leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-[#64748B] font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES (large 2-col cards) ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Key Features</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six features that power{' '}
              <span className="italic font-light text-[#0459F7]">every delivery.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">From booking to delivery confirmation — every stakeholder covered, every step automated.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group flex gap-5 p-7 rounded-2xl border border-[rgba(15,23,42,0.07)] hover:border-transparent hover:shadow-lg transition-all duration-300`}
                style={{ background: i % 2 === 0 ? '#FAFAFA' : 'white' }}
              >
                {/* Left: num + icon */}
                <div className="flex flex-col items-center gap-2 flex-shrink-0">
                  <span
                    className="font-mono-custom text-[10px] font-semibold w-6 h-6 rounded-full flex items-center justify-center text-white"
                    style={{ background: f.color }}
                  >
                    {f.num}
                  </span>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                    style={{ background: f.bg }}
                  >
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} style={{ color: f.color }} />
                  </div>
                </div>
                {/* Right: text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-display text-base font-semibold text-[#0F172A] leading-snug">{f.label}</h3>
                  </div>
                  <p className="text-sm text-[#475569] leading-relaxed mb-3">{f.desc}</p>
                  <span
                    className="inline-block text-[10px] font-semibold px-2.5 py-1 rounded-full"
                    style={{ background: f.bg, color: f.color }}
                  >
                    {f.tag}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ORDER FLOW — vertical timeline ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Order Flow</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              From order to delivery{' '}
              <span className="italic font-light text-[#0459F7]">in minutes.</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {orderSteps.map((step, i) => (
              <div key={step.title} className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} flex gap-6 mb-2`}>
                {/* Left: icon + connector line */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center z-10 flex-shrink-0 shadow-md"
                    style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}
                  >
                    <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                  </div>
                  {i < orderSteps.length - 1 && (
                    <div className="w-0.5 flex-1 my-2" style={{ background: 'linear-gradient(to bottom, rgba(4,89,247,0.3), rgba(4,89,247,0.06))' }} />
                  )}
                </div>

                {/* Right: content */}
                <div className={`flex-1 bg-white rounded-2xl p-6 border border-[rgba(15,23,42,0.07)] hover:border-[rgba(4,89,247,0.2)] hover:shadow-md transition-all ${i < orderSteps.length - 1 ? 'mb-3' : ''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                          style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}
                        >
                          STEP {i + 1}
                        </span>
                        <h3 className="font-display text-lg font-semibold text-[#0F172A]">{step.title}</h3>
                      </div>
                      <p className="text-sm text-[#475569] leading-relaxed">{step.desc}</p>
                    </div>
                    <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold text-[#0459F7] whitespace-nowrap" style={{ background: 'rgba(4,89,247,0.08)' }}>
                      {step.detail}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3 STAKEHOLDERS ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Platform Apps</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Three apps, built for every{' '}
              <span className="italic font-light text-[#0459F7]">stakeholder.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">Every role gets a purpose-built experience — not a one-size-fits-all tool.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">

            {/* Customer App */}
            <div className="animate-on-scroll stagger-2 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="DevicePhoneMobileIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Customer App</h3>
                    <p className="text-white/60 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 flex flex-col gap-2 bg-[#F8FAFF]">
                {customerFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white border border-[rgba(4,89,247,0.06)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(4,89,247,0.12)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#0459F7]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Driver App */}
            <div className="animate-on-scroll stagger-3 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6 bg-[#0F172A]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                    <Icon name="UserIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Driver App</h3>
                    <p className="text-white/50 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 flex flex-col gap-2 bg-[#F8F8F8]">
                {driverFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white border border-[rgba(15,23,42,0.06)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center bg-[#0F172A]">
                      <Icon name="CheckIcon" size={9} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Merchant Portal */}
            <div className="animate-on-scroll stagger-4 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="BuildingStorefrontIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Merchant Portal</h3>
                    <p className="text-white/60 text-xs">Web Platform</p>
                  </div>
                </div>
              </div>
              <div className="p-5 flex flex-col gap-2 bg-[#FAF8FF]">
                {merchantFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white border border-[rgba(124,58,237,0.07)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.12)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#7C3AED]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── WHATSAPP BOOKING ── */}
      <div className="py-24 bg-[#F0FDF4]" style={{ borderTop: '1px solid rgba(22,163,74,0.12)', borderBottom: '1px solid rgba(22,163,74,0.12)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(22,163,74,0.3)] bg-[rgba(22,163,74,0.08)] text-[11px] font-semibold tracking-widest text-[#16A34A] uppercase mb-5">
                <Icon name="ChatBubbleLeftRightIcon" size={12} className="text-[#16A34A]" />
                WhatsApp Booking
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-5">
                Book a delivery without{' '}
                <span className="italic font-light text-[#16A34A]">downloading any app.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                Customers use the WhatsApp they already have. Your chatbot handles the full booking flow — pickup, drop, fare, and live tracking — conversationally.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Easy & Convenient', 'Instant Response', 'No App Download', 'Saves Time', 'Wide User Reach', 'Cost Effective'].map((b) => (
                  <div key={b} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-[rgba(22,163,74,0.15)]">
                    <Icon name="CheckCircleIcon" size={14} className="text-[#16A34A]" />
                    <span className="text-xs font-semibold text-[#0F172A]">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — WhatsApp chat steps */}
            <div className="animate-on-scroll stagger-2">
              <div className="bg-white rounded-2xl border border-[rgba(22,163,74,0.15)] overflow-hidden shadow-sm">
                {/* Chat header */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(22,163,74,0.1)]" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                    <Icon name="ChatBubbleLeftRightIcon" size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Movio Courier</p>
                    <p className="text-xs text-white/70">Typically replies instantly</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-white/80" />
                    <span className="text-xs text-white/70 font-medium">Online</span>
                  </div>
                </div>

                {/* Steps */}
                <div className="p-5 flex flex-col gap-3">
                  {whatsappSteps.map((s, i) => (
                    <div key={s.num} className={`flex items-start gap-3 ${i % 2 === 0 ? 'self-start' : 'flex-row-reverse self-end'}`}>
                      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                        {s.num}
                      </div>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm font-medium max-w-[220px] ${i % 2 === 0 ? 'bg-[#F5F5F5] text-[#0F172A] rounded-tl-sm' : 'text-white rounded-tr-sm'}`} style={i % 2 !== 0 ? { background: 'linear-gradient(135deg, #25D366, #128C7E)' } : {}}>
                        {s.text}
                      </div>
                    </div>
                  ))}
                  <p className="text-center text-xs text-[#64748B] mt-2 font-medium">✓ Delivery confirmed. Driver on the way.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── PAYMENT METHODS ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Payment & Settlement</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Every payment method.{' '}
              <span className="italic font-light text-[#0459F7]">Automated settlement.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Accept payments the way your customers prefer — and pay drivers automatically, every day.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {paymentMethods.map((p, i) => (
              <div key={p.label} className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group flex items-center gap-5 p-6 rounded-2xl border border-[rgba(15,23,42,0.07)] bg-[#F5F5F5] hover:border-[rgba(4,89,247,0.2)] hover:bg-white hover:shadow-md transition-all duration-300`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.12), rgba(10,47,216,0.06))' }}>
                  <Icon name={p.icon as Parameters<typeof Icon>[0]['name']} size={24} className="text-[#0459F7]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A] text-base">{p.label}</p>
                  <p className="text-sm text-[#64748B] mt-0.5">{p.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Settlement highlight */}
          <div className="animate-on-scroll stagger-4 rounded-2xl p-8 border border-[rgba(4,89,247,0.15)]" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.04), rgba(10,47,216,0.02))' }}>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              {[
                { icon: 'BoltIcon', title: 'Instant UPI Collect', desc: 'Zero friction at checkout. Customer pays in seconds.' },
                { icon: 'ArrowPathIcon', title: 'Auto Driver Payout', desc: 'Daily or weekly automated settlement to driver wallet.' },
                { icon: 'DocumentTextIcon', title: 'Merchant Invoicing', desc: 'Auto-generated PDF invoices and monthly billing reports.' },
              ].map((item) => (
                <div key={item.title} className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                    <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-white" />
                  </div>
                  <p className="font-semibold text-[#0F172A] mb-1">{item.title}</p>
                  <p className="text-sm text-[#475569]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── ADDITIONAL FEATURES ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <span className="section-label block mb-3">More Capabilities</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Everything else your{' '}
              <span className="italic font-light text-[#0459F7]">delivery business needs.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {additionalFeatures.map((f, i) => (
              <div key={f.label} className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} flex items-center gap-4 p-6 bg-white rounded-2xl border border-[rgba(15,23,42,0.07)] hover:border-[rgba(4,89,247,0.2)] hover:shadow-md transition-all duration-300 group`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.12), rgba(10,47,216,0.06))' }}>
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0459F7]" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0F172A]">{f.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trust bar */}
          <div className="animate-on-scroll stagger-4 mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Secure & Reliable', 'White-label Ready', 'Real-time Tracking', 'Cloud-based Platform'].map((item) => (
              <div key={item} className="flex items-center gap-2 p-3 bg-white rounded-xl border border-[rgba(15,23,42,0.07)]">
                <Icon name="CheckBadgeIcon" size={16} className="text-[#0459F7] flex-shrink-0" />
                <span className="text-xs font-medium text-[#475569]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0A2FD8 0%, #0459F7 50%, #1B8DFF 100%)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', transform: 'translate(40px, -40px)' }}
            />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              <Icon name="BoltIcon" size={11} className="text-[#F59E0B]" />
              Ready to Launch?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Your branded courier platform,{' '}
              <span className="italic font-light text-white/80">live in 21 days.</span>
            </h2>

            <p className="relative text-white/70 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/75 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={15} />
                +91 9600830488
              </a>
              <span className="text-white/30 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/75 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={15} />
                contact@movio.in
              </a>
              <span className="text-white/30 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/75 font-medium">
                <Icon name="GlobeAltIcon" size={15} />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-white text-[#0459F7] hover:bg-white/90 shadow-lg transition-all duration-200 hover:-translate-y-0.5"
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/35 hover:border-white/70 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
